<script setup lang="ts">
import { formatMoneyWithSymbol } from '@/lib/money'
import { computed, ref, Ref } from 'vue'
import { catalogApi } from '@/api/client'
import type { OnlineItemFilters } from '@/api/client'
import { onBeforeRouteUpdate } from 'vue-router'
import { useUserStore } from '@/stores/UserStore'
import { useI18n } from 'vue-i18n'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
    Smartphone,
    Laptop,
    Watch,
    Tablet,
    Gamepad2,
    ExternalLink,
    ArrowLeft,
    Tag,
    Zap,
    ZapOff,
    LoaderCircle,
} from 'lucide-vue-next'
import Grade from '@/components/Grade.vue'
import Battery from '@/components/Battery.vue'
import BrandLogo from '@/components/BrandLogo.vue'
import MarketPricesCard from '@/components/MarketPricesCard.vue'
import CompetitorPricesCard from '@/components/CompetitorPricesCard.vue'
import PriceHistoryCard from '@/components/PriceHistoryCard.vue'
import OnlineItemsCard from '@/components/OnlineItemsCard.vue'
import GradeVariants from '@/components/GradeVariants.vue'
import SideHeading from '@/components/SideHeading.vue'
import { toast } from 'vue-sonner'
import type {
    MarketPricesResponse,
    SuggestedPriceResponse,
    SuggestedPriceSideBlock,
    CurrencyPriceBlock,
    CompetitorGrade,
    ChartResponse,
    OnlineItem,
    OnlineItemsResponse,
    CompetitorPrice,
} from './catalogDetailTypes'
import type { CatalogItem } from '@/types'

const { t } = useI18n()
const userStore = useUserStore()

const data: Ref<CatalogItem | null> = ref(null)
const suggestedPrice = ref<SuggestedPriceResponse | null>(null)
const gradesBySide = ref<Record<string, CompetitorGrade[]>>({})
const resellMarketData = ref<MarketPricesResponse | null>(null)
const buybackMarketData = ref<MarketPricesResponse | null>(null)
const loadingSuggestedPrice = ref(false)
const loadingCompetitorGrades = ref(false)
const loadingResellPrices = ref(false)
const loadingBuybackPrices = ref(false)
const togglingDynamicPrice = ref(false)
const competitorPrices = ref<CompetitorPrice[]>([])
const loadingCompetitorPrices = ref(false)
const isDynamicPriceActive = computed(() => data.value?.automatic_price_enabled ?? false)

/**
 * The side the item's own price belongs to — what `catalog_items.price` *means* on
 * this account, which `catalog_type` is the only record of. It says nothing about
 * which markets the account is compared against: that is `by_side`.
 */
const declaredPriceSide = computed(() => userStore.configuration?.catalog_type ?? null)

/** One block per side the account's vendors cover, each with its own figures. */
const sideBlocks = computed(() => suggestedPrice.value?.by_side ?? [])

/**
 * The API enumerates every currency the account's vendors cover, whether or not this
 * item has data in it — coverage, not availability. A CHF line of dashes is not a
 * price, so it does not earn a row; the currency is still named under the table, or
 * the reader would have no way to tell "no CHF data for this item" from "this account
 * has no CHF vendors at all".
 */
const hasFigures = (prices: CurrencyPriceBlock) =>
    prices.suggested_price !== null ||
    prices.median_market_price !== null ||
    prices.daily_avg_price !== null ||
    prices.top_competitor !== null

const pricedCurrencies = (block: SuggestedPriceSideBlock) => block.prices_by_currency.filter(hasFigures)

const unpricedCurrencies = (block: SuggestedPriceSideBlock) =>
    block.prices_by_currency.filter((prices) => !hasFigures(prices)).map((prices) => prices.currency)

/**
 * Read off the item itself, not off the suggested-price response: `currency` is null
 * exactly when the selling currency is undeclared (ADR-004), and `loadSuggestedPrice`
 * swallows a failure into `null` — so sourcing it there let one transient 500 answer
 * "no selling currency" and quietly remove the control below.
 */
const hasSellingCurrency = computed(() => !!data.value?.currency)

/**
 * Only the resell side is ever pushed to the customer's store, and an item with no
 * single selling currency has no unambiguous number to push.
 */
const canToggleDynamicPrice = computed(() => declaredPriceSide.value === 'resell' && hasSellingCurrency.value)

/**
 * Every currency's market, all of them rendered. Currency partitions the figures but
 * does not narrow the page: there is no view selector, because selecting one would
 * mean hiding a market the account genuinely trades in.
 */
const resellBlocks = computed(() => resellMarketData.value?.by_currency ?? [])
const buybackBlocks = computed(() => buybackMarketData.value?.by_currency ?? [])

/**
 * The sides the whole page sections itself by. `by_side` is the account's own answer,
 * built from the vendors it is compared against, so every card follows it rather than
 * assuming both markets. Should that request fail, both are shown: an empty section is
 * a smaller lie than a market silently missing from the page.
 */
const coveredSides = computed<('resell' | 'buyback')[]>(() =>
    sideBlocks.value.length > 0 ? sideBlocks.value.map((block) => block.side) : ['resell', 'buyback'],
)

/**
 * Both markets under one card, sectioned by side the way the suggested prices card
 * is. Each side keeps its own loading flag: the two are fetched separately, so one
 * still renders while the other is in flight.
 */
const marketSides = computed(() =>
    coveredSides.value.map((side) => ({
        side,
        blocks: side === 'resell' ? resellBlocks.value : buybackBlocks.value,
        loading: side === 'resell' ? loadingResellPrices.value : loadingBuybackPrices.value,
    })),
)

const props = defineProps<{
    id: number
}>()

const loadDetail = async (id: number) => {
    try {
        const response = await catalogApi.get(id)
        data.value = response.data as CatalogItem
    } catch {}
}

const loadMarketPrices = async (id: number) => {
    loadingResellPrices.value = true
    loadingBuybackPrices.value = true

    // Started together, not one after the other: they are independent endpoints, and
    // awaiting the first in series is what made the second card's skeleton outlast it.
    await Promise.allSettled([
        (async () => {
            try {
                const response = await catalogApi.resellMarketPrices(id)
                resellMarketData.value = response.data as MarketPricesResponse
            } catch {
                resellMarketData.value = null
            } finally {
                loadingResellPrices.value = false
            }
        })(),
        (async () => {
            try {
                const response = await catalogApi.buybackMarketPrices(id)
                buybackMarketData.value = response.data as MarketPricesResponse
            } catch {
                buybackMarketData.value = null
            } finally {
                loadingBuybackPrices.value = false
            }
        })(),
    ])
}

const loadSuggestedPrice = async (id: number) => {
    loadingSuggestedPrice.value = true
    try {
        const response = await catalogApi.suggestedPrice(id)
        suggestedPrice.value = response.data as SuggestedPriceResponse
    } catch {
        suggestedPrice.value = null
    } finally {
        loadingSuggestedPrice.value = false
    }
}

const loadCompetitorGrades = async (id: number) => {
    loadingCompetitorGrades.value = true
    try {
        const response = await catalogApi.competitorGrades(id)
        gradesBySide.value = response.data as Record<string, CompetitorGrade[]>
    } catch {
        gradesBySide.value = {}
    } finally {
        loadingCompetitorGrades.value = false
    }
}

/**
 * What the competitors on each side charge, next to how their stock is graded — the
 * two halves of the same market, so they are sectioned by side together.
 */
const competitorSides = computed(() =>
    coveredSides.value.map((side) => ({
        side,
        prices: competitorPrices.value.filter((price) => price.vendor.type === side),
    })),
)

const toggleDynamicPrice = async () => {
    togglingDynamicPrice.value = true
    try {
        await catalogApi.toggleDynamicPrice(props.id)
        if (data.value) {
            data.value.automatic_price_enabled = !data.value.automatic_price_enabled
        }
        toast.success(
            t(
                isDynamicPriceActive.value
                    ? 'catalog.details.automaticPricingMessage.enabled'
                    : 'catalog.details.automaticPricingMessage.disabled',
            ),
        )
    } catch {
        toast.error(t('common.errors.somethingWrong'))
    } finally {
        togglingDynamicPrice.value = false
    }
}

// Chart & online items state
const resellChartData = ref<ChartResponse | null>(null)
const buybackChartData = ref<ChartResponse | null>(null)
const resellOnlineItems = ref<OnlineItem[]>([])
const buybackOnlineItems = ref<OnlineItem[]>([])
const resellOnlineTotal = ref(0)
const buybackOnlineTotal = ref(0)
const resellOnlinePage = ref(0)
const buybackOnlinePage = ref(0)
const loadingResellChart = ref(false)
const loadingBuybackChart = ref(false)
const loadingResellItems = ref(false)
const loadingBuybackItems = ref(false)
const loadingMoreResellItems = ref(false)
const loadingMoreBuybackItems = ref(false)

/** What the listing is narrowed to, and everything it could be narrowed to. */
const resellItemsFilters = ref<OnlineItemFilters>({ country: null, currency: null })
const buybackItemsFilters = ref<OnlineItemFilters>({ country: null, currency: null })
const resellItemCountries = ref<string[]>([])
const buybackItemCountries = ref<string[]>([])
const resellItemCurrencies = ref<string[]>([])
const buybackItemCurrencies = ref<string[]>([])
const loadResellChart = async (id: number) => {
    loadingResellChart.value = true
    try {
        const response = await catalogApi.resellChart(id)
        resellChartData.value = response.data as ChartResponse
    } catch {
        resellChartData.value = null
    } finally {
        loadingResellChart.value = false
    }
}

const loadBuybackChart = async (id: number) => {
    loadingBuybackChart.value = true
    try {
        const response = await catalogApi.buybackChart(id)
        buybackChartData.value = response.data as ChartResponse
    } catch {
        buybackChartData.value = null
    } finally {
        loadingBuybackChart.value = false
    }
}

/**
 * ADR-016: the customer's own series is labelled a selling price on both charts, so
 * the trade-in one reads as a comparison rather than as an offer they make.
 */
const ownPriceLabel = computed(() => t('catalog.details.chart.ownPrice'))

const loadResellOnlineItems = async (id: number, page: number = 0) => {
    if (page === 0) {
        loadingResellItems.value = true
    } else {
        loadingMoreResellItems.value = true
    }
    try {
        const response = await catalogApi.resellItems<OnlineItemsResponse>(id, page, resellItemsFilters.value)
        if (page === 0) {
            resellOnlineItems.value = response.data.items
        } else {
            resellOnlineItems.value = [...resellOnlineItems.value, ...response.data.items]
        }
        resellOnlineTotal.value = response.data.total
        resellItemCountries.value = response.data.countries ?? []
        resellItemCurrencies.value = response.data.currencies ?? []
        resellOnlinePage.value = page
    } catch {
        if (page === 0) resellOnlineItems.value = []
    } finally {
        loadingResellItems.value = false
        loadingMoreResellItems.value = false
    }
}

/**
 * The listing is paged server-side, so a narrowing is a new first page rather than a
 * filter over the rows already fetched — those are only the cheapest fifteen.
 */
const narrowResellItems = (filters: OnlineItemFilters) => {
    Object.assign(resellItemsFilters.value, filters)
    loadResellOnlineItems(props.id, 0)
}

const loadBuybackOnlineItems = async (id: number, page: number = 0) => {
    if (page === 0) {
        loadingBuybackItems.value = true
    } else {
        loadingMoreBuybackItems.value = true
    }
    try {
        const response = await catalogApi.buybackItems<OnlineItemsResponse>(id, page, buybackItemsFilters.value)
        if (page === 0) {
            buybackOnlineItems.value = response.data.items
        } else {
            buybackOnlineItems.value = [...buybackOnlineItems.value, ...response.data.items]
        }
        buybackOnlineTotal.value = response.data.total
        buybackItemCountries.value = response.data.countries ?? []
        buybackItemCurrencies.value = response.data.currencies ?? []
        buybackOnlinePage.value = page
    } catch {
        if (page === 0) buybackOnlineItems.value = []
    } finally {
        loadingBuybackItems.value = false
        loadingMoreBuybackItems.value = false
    }
}

/** @see narrowResellItems() — same reason. */
const narrowBuybackItems = (filters: OnlineItemFilters) => {
    Object.assign(buybackItemsFilters.value, filters)
    loadBuybackOnlineItems(props.id, 0)
}

const loadCompetitorPrices = async (id: number) => {
    loadingCompetitorPrices.value = true
    try {
        const response = await catalogApi.competitorPrices(id)
        competitorPrices.value = response.data as CompetitorPrice[]
    } catch {
        competitorPrices.value = []
    } finally {
        loadingCompetitorPrices.value = false
    }
}

const hasMoreResellItems = computed(() => resellOnlineItems.value.length < resellOnlineTotal.value)
const hasMoreBuybackItems = computed(() => buybackOnlineItems.value.length < buybackOnlineTotal.value)

const currentUniqueKey = computed(() => data.value?.unique_key ?? '')

const category = computed(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const item = data.value as Record<string, any> | undefined

    return (item?.category as { name: string; slug: string } | undefined) ?? null
})

const loadAll = (id: number) => {
    // The component is reused across items — GradeVariants links to siblings in-page —
    // so the listing filters have to be dropped explicitly. A country the previous item
    // traded in may not be among this one's, which would leave the chips reading "All"
    // over a table the server had still narrowed.
    resellItemsFilters.value = { country: null, currency: null }
    buybackItemsFilters.value = { country: null, currency: null }

    loadDetail(id)
    loadSuggestedPrice(id)
    loadCompetitorGrades(id)
    loadCompetitorPrices(id)
    loadMarketPrices(id)
    loadResellChart(id)
    loadBuybackChart(id)
    loadResellOnlineItems(id)
    loadBuybackOnlineItems(id)
}

onBeforeRouteUpdate(async (to, from) => {
    if (to.params.id !== from.params.id) {
        loadAll(Number(to.params.id))
    }
})

loadAll(props.id)
</script>

<template>
    <div class="space-y-4">
        <div class="-mx-6 sm:-mx-8 border-b px-6 sm:px-8 pb-4">
            <router-link
                to="/dashboard/catalog"
                class="mb-3 inline-flex items-center text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
                <ArrowLeft class="size-4 mr-1" />
                {{ t('catalog.details.backToCatalog') }}
            </router-link>

            <div class="flex flex-wrap items-start justify-between gap-2">
                <div class="min-w-0 max-w-full">
                    <h1 class="flex items-center gap-3 text-2xl font-bold tracking-tight sm:text-3xl">
                        <BrandLogo v-if="data?.brand_name" :brand="data.brand_name" colored class="size-7" />
                        {{ data?.name }}

                        <a
                            v-if="data?.url"
                            :href="data.url"
                            target="_blank"
                            class="inline-flex text-muted-foreground hover:text-foreground transition-colors align-middle ml-1"
                        >
                            <ExternalLink class="size-5" />
                        </a>
                    </h1>

                    <div v-if="data" class="flex flex-wrap items-center gap-4 mt-2 text-sm text-muted-foreground">
                        <span class="inline-flex items-center gap-1.5">
                            Grade: <Grade :grade="data.grade" plain class="font-medium text-foreground" />
                        </span>

                        <span class="inline-flex items-center gap-1.5">
                            Battery: <Battery :status="data.battery_type" class="text-foreground font-medium" />
                        </span>

                        <template v-if="data.details">
                            <span v-for="(value, name) in data.details" :key="name" class="capitalize">
                                {{ name }}: <span class="font-medium text-foreground uppercase">{{ value }}</span>
                            </span>
                        </template>
                    </div>
                </div>

                <div class="flex flex-wrap items-center gap-3">
                    <Badge v-if="category" variant="outline">
                        <Smartphone v-if="category.slug === 'smartphone'" class="size-3 mr-1" />
                        <Laptop v-else-if="category.slug === 'pc'" class="size-3 mr-1" />
                        <Watch v-else-if="category.slug === 'smartwatch'" class="size-3 mr-1" />
                        <Tablet v-else-if="category.slug === 'tablet'" class="size-3 mr-1" />
                        <Gamepad2 v-else-if="category.slug === 'console'" class="size-3 mr-1" />
                        {{ category.name }}
                    </Badge>
                </div>
            </div>
        </div>
    </div>

    <div class="max-w-6xl space-y-6 mt-6">
        <GradeVariants
            :sides="coveredSides"
            :grades-by-side="gradesBySide"
            :loading="loadingCompetitorGrades"
            :current-unique-key="currentUniqueKey"
        />

        <!-- Pricing — a section per side, each with a row per currency -->
        <Card>
            <CardHeader class="flex flex-row items-center justify-between space-y-0">
                <CardTitle class="flex items-center gap-2">
                    <Tag class="size-4" />
                    {{ t('catalog.details.pricing.title') }}
                </CardTitle>
                <Button
                    v-if="data && canToggleDynamicPrice"
                    size="sm"
                    variant="secondary"
                    :disabled="togglingDynamicPrice"
                    @click="toggleDynamicPrice"
                >
                    <LoaderCircle v-if="togglingDynamicPrice" class="size-3.5 animate-spin" />
                    <template v-else>
                        <Zap v-if="!isDynamicPriceActive" class="size-3.5" />
                        <ZapOff v-else class="size-3.5" />
                        {{
                            t(
                                isDynamicPriceActive
                                    ? 'catalog.details.pricing.disableDynamic'
                                    : 'catalog.details.pricing.enableDynamic',
                            )
                        }}
                    </template>
                </Button>
            </CardHeader>
            <CardContent>
                <template v-if="loadingSuggestedPrice">
                    <div class="space-y-3">
                        <Skeleton class="h-5 w-full" />
                        <Skeleton class="h-5 w-full" />
                        <Skeleton class="h-5 w-full" />
                    </div>
                </template>
                <template v-else-if="sideBlocks.length === 0">
                    <p class="text-sm text-muted-foreground">{{ t('catalog.details.pricing.empty') }}</p>
                </template>
                <template v-else>
                    <!--
                        One section per side, each with its own header, one row per currency
                        inside it. Nothing here is ever summed or compared across a section
                        or a row: a suggested price only means something next to the market
                        median of its own side and its own currency.

                        Both tables carry the same columns, so a figure sits under the same
                        heading whichever side it belongs to and the eye can travel straight
                        down. That works because min and max are the *market's* observed
                        range — a fact both sides have — rather than the strategy's allowed
                        range, which only resell expresses.
                    -->
                    <div class="space-y-6">
                        <div v-for="block in sideBlocks" :key="block.side">
                            <SideHeading :side="block.side" class="mb-2" />

                            <p v-if="pricedCurrencies(block).length === 0" class="text-xs text-muted-foreground">
                                {{ t('catalog.details.pricing.emptySide') }}
                            </p>

                            <!--
                                Same columns is not the same layout: each side is its own
                                table, so with auto layout the browser sizes their columns
                                from their own contents and a longer competitor name or a
                                four-digit price shifts that side's grid out of step with
                                the one above it. Fixed widths, declared once here and
                                identical on both, are what actually lines them up.

                                Proportional rather than absolute, because absolute widths
                                leave the slack to whichever column has none — the last one
                                absorbed every spare pixel on a wide screen and rendered a
                                vendor name in a column half the card wide. Shares spread
                                the extra across all six, and still line the tables up
                                exactly, since a percentage of one card is a percentage of
                                the other.
                            -->
                            <Table v-else class="table-fixed">
                                <TableHeader>
                                    <TableRow class="hover:bg-transparent">
                                        <TableHead class="w-[10%]" />
                                        <TableHead class="w-[18%] text-right">{{
                                            t('catalog.details.pricing.suggested')
                                        }}</TableHead>
                                        <TableHead class="w-[18%] text-right">{{
                                            t('catalog.details.pricing.overallAvg')
                                        }}</TableHead>
                                        <TableHead class="w-[16%] text-right">{{
                                            t('catalog.details.pricing.overallMin')
                                        }}</TableHead>
                                        <TableHead class="w-[16%] text-right">{{
                                            t('catalog.details.pricing.overallMax')
                                        }}</TableHead>
                                        <TableHead class="w-[22%] pl-6">{{
                                            t('catalog.details.pricing.topCompetitor')
                                        }}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    <TableRow
                                        v-for="prices in pricedCurrencies(block)"
                                        :key="`${block.side}-${prices.currency}`"
                                    >
                                        <!-- Named the same way the market prices card names it, so a
                                             suggested price and the market it came from read as a pair. -->
                                        <TableCell>
                                            <Badge variant="outline" class="tracking-wider">{{
                                                prices.currency
                                            }}</Badge>
                                        </TableCell>
                                        <TableCell class="text-right font-semibold tabular-nums">
                                            {{
                                                formatMoneyWithSymbol(prices.suggested_price, prices.currency_symbol, {
                                                    fallback: '—',
                                                })
                                            }}
                                        </TableCell>
                                        <TableCell class="text-right tabular-nums text-muted-foreground">
                                            {{
                                                formatMoneyWithSymbol(prices.daily_avg_price, prices.currency_symbol, {
                                                    fallback: '—',
                                                })
                                            }}
                                        </TableCell>
                                        <TableCell class="text-right tabular-nums text-muted-foreground">
                                            {{
                                                formatMoneyWithSymbol(prices.daily_min_price, prices.currency_symbol, {
                                                    fallback: '—',
                                                })
                                            }}
                                        </TableCell>
                                        <TableCell class="text-right tabular-nums text-muted-foreground">
                                            {{
                                                formatMoneyWithSymbol(prices.daily_max_price, prices.currency_symbol, {
                                                    fallback: '—',
                                                })
                                            }}
                                        </TableCell>
                                        <!--
                                            Belongs to this row, not to the side: the cheapest
                                            listing is only meaningful among listings priced in
                                            the same currency.
                                        -->
                                        <TableCell class="pl-6">
                                            <template v-if="prices.daily_top_competitor">
                                                <div class="flex min-w-0 items-center gap-2">
                                                    <span class="truncate text-sm">{{
                                                        prices.daily_top_competitor.vendor
                                                    }}</span>
                                                    <Badge variant="secondary" class="shrink-0 uppercase">{{
                                                        prices.daily_top_competitor.country
                                                    }}</Badge>
                                                </div>
                                                <div
                                                    v-if="prices.daily_top_competitor.reseller"
                                                    class="truncate text-xs text-muted-foreground"
                                                >
                                                    {{ prices.daily_top_competitor.reseller }}
                                                </div>
                                            </template>
                                            <span v-else class="text-muted-foreground">—</span>
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>

                            <p v-if="unpricedCurrencies(block).length > 0" class="mt-2 text-xs text-muted-foreground">
                                {{
                                    t('catalog.details.pricing.noDataForCurrencies', {
                                        currencies: unpricedCurrencies(block).join(', '),
                                    })
                                }}
                            </p>
                        </div>
                    </div>
                </template>
            </CardContent>
        </Card>

        <MarketPricesCard :sides="marketSides" />

        <CompetitorPricesCard :sides="competitorSides" :loading-prices="loadingCompetitorPrices" />

        <!--
            Each market gets its own band: every card in it names the side itself,
            and the padding keeps the two bands from reading as one long strip.
        -->
        <div class="space-y-4 pt-6">
            <PriceHistoryCard
                :key="`resell-history-${props.id}`"
                side="resell"
                :data="resellChartData"
                :loading="loadingResellChart"
                :own-price-label="ownPriceLabel"
            />

            <OnlineItemsCard
                side="resell"
                :items="resellOnlineItems"
                :total="resellOnlineTotal"
                :loading="loadingResellItems"
                :loading-more="loadingMoreResellItems"
                :has-more="hasMoreResellItems"
                :countries="resellItemCountries"
                :currencies="resellItemCurrencies"
                :country="resellItemsFilters.country ?? null"
                :currency="resellItemsFilters.currency ?? null"
                @update:filters="narrowResellItems($event)"
                @load-more="loadResellOnlineItems(props.id, resellOnlinePage + 1)"
            />
        </div>

        <!-- Trade-in Section: Chart + Online Items -->
        <div class="space-y-4 pt-6">
            <PriceHistoryCard
                :key="`buyback-history-${props.id}`"
                side="buyback"
                :data="buybackChartData"
                :loading="loadingBuybackChart"
                :own-price-label="ownPriceLabel"
            />

            <OnlineItemsCard
                side="buyback"
                :items="buybackOnlineItems"
                :total="buybackOnlineTotal"
                :loading="loadingBuybackItems"
                :loading-more="loadingMoreBuybackItems"
                :has-more="hasMoreBuybackItems"
                :countries="buybackItemCountries"
                :currencies="buybackItemCurrencies"
                :country="buybackItemsFilters.country ?? null"
                :currency="buybackItemsFilters.currency ?? null"
                @update:filters="narrowBuybackItems($event)"
                @load-more="loadBuybackOnlineItems(props.id, buybackOnlinePage + 1)"
            />
        </div>
    </div>
</template>
